// Блок 1 «Базовые расчёты» — калькуляторы 1–9 прототипа.
// Чистые функции; текстовые подписи собирает UI по возвращаемым кодам.

import { NEAR_REFERENCE_DISTANCE_M } from './constants';
import {
  decentrationPerEye,
  edgeCenterThickness,
  prismByPrentice,
  sphericalEquivalent,
  type LensThickness,
  type Rx,
} from './core';

// --- 1. Транспозиция цилиндра -------------------------------------------------

/**
 * Транспозиция: sph' = sph + cyl; cyl' = −cyl; ось' = ось ± 90°.
 *
 * Ось нормализуется в диапазон (0°, 180°]: ось 0° и 180° оптически совпадают,
 * канонической считаем 180°. Из-за этого двойная транспозиция возвращает
 * исходный рецепт с точностью до этой нормализации.
 */
export function transpose({ sphere, cyl, axis }: Rx): Rx {
  let nextAxis = axis + 90;
  if (nextAxis > 180) nextAxis -= 180;
  if (nextAxis <= 0) nextAxis += 180;
  return { sphere: sphere + cyl, cyl: -cyl, axis: nextAxis };
}

// --- 2. Минимальный диаметр заготовки ----------------------------------------

export interface MinDiameterInput {
  /** Диагональ окна оправы (ED), мм. */
  ed: number;
  /** Расстояние между геометрическими центрами оправы (FPD), мм. */
  fpd: number;
  /** Межзрачковое расстояние пациента (PD), мм. */
  pd: number;
  /** Технологический запас на обработку, мм. */
  margin: number;
}

export interface MinDiameterResult {
  /** Децентрация на глаз, мм (по модулю). */
  decentration: number;
  /** Минимальный диаметр заготовки, мм. */
  minDiameter: number;
}

/** Dec = |FPD − PD| / 2; Ø_min = ED + 2·Dec + 2·запас. */
export function minimumBlankDiameter({ ed, fpd, pd, margin }: MinDiameterInput): MinDiameterResult {
  const decentration = Math.abs(fpd - pd) / 2;
  return { decentration, minDiameter: ed + 2 * decentration + 2 * margin };
}

// --- 3. Толщина линзы ---------------------------------------------------------

export interface LensThicknessInput {
  diameter: number;
  power: number;
  index: number;
  /** Опорная толщина: для минуса — по центру, для плюса — по краю, мм. */
  refThickness: number;
}

export interface LensThicknessResult extends LensThickness {
  /** Минусовая линза: опорная толщина — центр, считаем край (и наоборот). */
  isMinus: boolean;
  /** Искомая толщина, мм: край для минуса, центр для плюса. */
  result: number;
}

export function lensThickness({
  diameter,
  power,
  index,
  refThickness,
}: LensThicknessInput): LensThicknessResult {
  const isMinus = power < 0;
  const thickness = edgeCenterThickness(diameter, power, index, refThickness);
  return { ...thickness, isMinus, result: isMinus ? thickness.edge : thickness.center };
}

// --- 4. Децентрация и призма --------------------------------------------------

export type DecentrationDirection = 'temporal' | 'nasal';

export interface DecentrationPrismInput {
  /** Сила линзы, дптр. */
  power: number;
  /** A — ширина проёма оправы, мм. */
  a: number;
  /** DBL — переносица, мм. */
  dbl: number;
  /** Монокулярный PD, мм. */
  monoPd: number;
}

export interface DecentrationPrismResult {
  /** Величина децентрации, мм (по модулю). */
  decentration: number;
  direction: DecentrationDirection;
  /** Индуцированная призма, Δ. */
  prism: number;
}

export function decentrationPrism({
  power,
  a,
  dbl,
  monoPd,
}: DecentrationPrismInput): DecentrationPrismResult {
  const signed = decentrationPerEye(a, dbl, monoPd);
  return {
    decentration: Math.abs(signed),
    direction: signed >= 0 ? 'temporal' : 'nasal',
    prism: prismByPrentice(power, signed),
  };
}

// --- 5. Вертексная поправка ---------------------------------------------------

export interface VertexInput {
  /** Исходная сила, дптр. */
  power: number;
  /** Старое вертексное расстояние, мм. */
  oldVertex: number;
  /** Новое вертексное расстояние, мм. */
  newVertex: number;
}

/**
 * Пересчёт силы на другое вертексное расстояние: F' = F / (1 − d·F),
 * где d — НА СКОЛЬКО линза приблизилась к глазу, в метрах.
 *
 * Проверка по справочным таблицам: −10.00 дптр с 12 мм на роговицу → −8.93;
 * −6.00 с 12 мм → −5.60. Отдаём `null`, если знаменатель обнулился
 * (физически недостижимо, но арифметически возможно).
 *
 * ОТКЛОНЕНИЕ ОТ ПРОТОТИПА (D2). Прототип брал `d = (new − old)/1000`, то есть
 * с обратным знаком относительно классической формулы, которую сам же приводил
 * в примечании. Для пресета «Очки → КЛ» (−6.00, 12 → 0 мм) он выдавал −6.47
 * вместо −5.60 — поправка не только неверная по величине, но и в другую
 * сторону (минус усиливался вместо ослабления). Исправлено.
 */
export function vertexCompensation({ power, oldVertex, newVertex }: VertexInput): number | null {
  const shiftM = (oldVertex - newVertex) / 1000;
  const denominator = 1 - shiftM * power;
  if (denominator === 0 || !Number.isFinite(denominator)) return null;
  return power / denominator;
}

// --- 6. Сферический эквивалент ------------------------------------------------

export { sphericalEquivalent };

// --- 7. Сила в косом меридиане ------------------------------------------------

export interface ObliqueMeridianInput extends Rx {
  /** Искомый меридиан, °. */
  meridian: number;
}

/** Fθ = sph + cyl · sin²(θ − ось). */
export function obliqueMeridianPower({
  sphere,
  cyl,
  axis,
  meridian,
}: ObliqueMeridianInput): number {
  const theta = ((meridian - axis) * Math.PI) / 180;
  return sphere + cyl * Math.pow(Math.sin(theta), 2);
}

// --- 8. Близь / промежуточное расстояние --------------------------------------

export interface NearPowerInput {
  /** Рецепт для дали (сфера), дптр. */
  distancePower: number;
  /** Аддидация из рецепта (выписана для чтения с 40 см), дптр. */
  add: number;
  /** Рабочее расстояние, м. */
  workingDistance: number;
}

/**
 * Сфера для заданного рабочего расстояния:
 * F = F_даль + Add − (1/0.4 − 1/d), где d — рабочее расстояние в метрах.
 *
 * То есть аддидация уменьшается на разницу вергенций между «чтением» (40 см)
 * и фактическим расстоянием. Инвариант: при d = 40 см результат равен
 * F_даль + Add, то есть обычному рецепту для близи.
 *
 * ОТКЛОНЕНИЕ ОТ ПРОТОТИПА (D3). Прототип считал `F_даль + Add − 1/d`, из-за
 * чего (а) рецепт для чтения занижался на всю вергенцию 2.5 дптр (−1.50 вместо
 * +1.00 при F=−1.00 и Add=+2.00) и (б) чем ДАЛЬШЕ рабочее расстояние, тем
 * БОЛЬШЕ получалось плюса — обратная зависимость. Исправлено.
 */
export function nearPowerForDistance({
  distancePower,
  add,
  workingDistance,
}: NearPowerInput): number | null {
  if (!workingDistance || !Number.isFinite(workingDistance)) return null;
  const reduction = 1 / NEAR_REFERENCE_DISTANCE_M - 1 / workingDistance;
  return distancePower + add - reduction;
}

/** Грубая возрастная оценка аддидации (таблица прототипа, дптр). */
export function addByAge(age: number): number {
  if (age < 40) return 0;
  if (age < 45) return 1.0;
  if (age < 50) return 1.5;
  if (age < 55) return 2.0;
  if (age < 60) return 2.5;
  return 3.0;
}

// --- 9. Результирующая призма -------------------------------------------------

export type PrismBase = 'up' | 'down' | 'in' | 'out';

export interface PrismComponent {
  /** Величина, Δ. */
  magnitude: number;
  base: PrismBase;
}

export interface CompoundPrismResult {
  /** Величина результирующей призмы, Δ. */
  magnitude: number;
  /** Горизонтальная составляющая: `null`, если её нет. */
  horizontal: 'in' | 'out' | null;
  /** Вертикальная составляющая: `null`, если её нет. */
  vertical: 'up' | 'down' | null;
  /** Угол от горизонтали, °. */
  angleDeg: number;
}

function prismToXY({ magnitude, base }: PrismComponent): { x: number; y: number } {
  switch (base) {
    case 'in':
      return { x: -magnitude, y: 0 };
    case 'out':
      return { x: magnitude, y: 0 };
    case 'up':
      return { x: 0, y: magnitude };
    case 'down':
      return { x: 0, y: -magnitude };
  }
}

/**
 * Сложение двух призм: компоненты складываются по осям X (in/out) и Y (up/down),
 * величина — по теореме Пифагора, направление — через арктангенс.
 *
 * ОТКЛОНЕНИЕ ОТ ПРОТОТИПА (D5, косметическое). Прототип всегда печатал обе
 * составляющие базиса, поэтому чисто вертикальная призма подписывалась как
 * «Base Up-Out (≈90°)». Здесь нулевая составляющая отдаётся как `null`, и UI
 * её не показывает; сами числа не изменились.
 */
export function compoundPrism(first: PrismComponent, second: PrismComponent): CompoundPrismResult {
  const a = prismToXY(first);
  const b = prismToXY(second);
  const x = a.x + b.x;
  const y = a.y + b.y;
  return {
    magnitude: Math.sqrt(x * x + y * y),
    horizontal: x === 0 ? null : x > 0 ? 'out' : 'in',
    vertical: y === 0 ? null : y > 0 ? 'up' : 'down',
    angleDeg: (Math.atan2(Math.abs(y), Math.abs(x)) * 180) / Math.PI,
  };
}
